export const HEART_PROVINCE_SCHEMA = "vaelor-heart-province-topology/v1" as const;
export const HEART_PROVINCE_RADIUS = 12 as const;
export const HEART_PROVINCE_CELL_COUNT = 469 as const;

type AxialDirection = readonly [number, number];

const DIRECTIONS: readonly AxialDirection[] = Object.freeze([
  Object.freeze([1, 0] as const),
  Object.freeze([0, 1] as const),
  Object.freeze([-1, 1] as const),
  Object.freeze([-1, 0] as const),
  Object.freeze([0, -1] as const),
  Object.freeze([1, -1] as const)
]);

export const HEART_PROVINCE_DIRECTIONS = DIRECTIONS;

export type HeartProvinceFront = 1 | 2 | 3 | 4 | 5 | 6;

export type HeartProvinceRole =
  | "throne"
  | "citadel"
  | "gate"
  | "guardian"
  | "inner"
  | "pillar"
  | "camp"
  | "arrival"
  | "battlefield";

export interface HeartProvinceCoordinate {
  readonly q: number;
  readonly r: number;
}

export interface HeartProvinceCell extends HeartProvinceCoordinate {
  readonly id: string;
  readonly index: number;
  readonly distance: number;
  readonly front: HeartProvinceFront | null;
  readonly slot: number;
  readonly role: HeartProvinceRole;
}

export interface HeartProvinceTopology {
  readonly schema: typeof HEART_PROVINCE_SCHEMA;
  readonly radius: typeof HEART_PROVINCE_RADIUS;
  readonly cells: readonly HeartProvinceCell[];
}

export interface HeartProvinceSelector {
  readonly front?: HeartProvinceFront | null;
  readonly role?: HeartProvinceRole;
}

export const HEART_PROVINCE_ROLE_TOTALS: Readonly<
  Record<HeartProvinceRole, number>
> = Object.freeze({
  throne: 1,
  citadel: 6,
  gate: 6,
  guardian: 6,
  inner: 18,
  pillar: 12,
  camp: 24,
  arrival: 6,
  battlefield: 390
});

export function heartProvinceCellId(q: number, r: number): string {
  return "heart:" + q + ":" + r;
}

export function heartProvinceDistance(q: number, r: number): number {
  return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
}

export function heartProvinceDistanceBetween(
  from: HeartProvinceCoordinate,
  to: HeartProvinceCoordinate
): number {
  return heartProvinceDistance(from.q - to.q, from.r - to.r);
}

export const axialDistance = heartProvinceDistance;
export const hexDistance = heartProvinceDistanceBetween;

function canonicalZero(value: number): number {
  return Object.is(value, -0) ? 0 : value;
}

export function rotateHeartProvinceCoordinate(
  coordinate: HeartProvinceCoordinate
): HeartProvinceCoordinate {
  return Object.freeze({
    q: canonicalZero(-coordinate.r),
    r: canonicalZero(coordinate.q + coordinate.r)
  });
}

export function rotateHeartProvince(
  q: number,
  r: number
): HeartProvinceCoordinate {
  return rotateHeartProvinceCoordinate({ q, r });
}

export const rotate = rotateHeartProvince;

export function isHeartProvinceCoordinate(
  q: number,
  r: number
): boolean {
  return Number.isInteger(q) && Number.isInteger(r) &&
    heartProvinceDistance(q, r) <= HEART_PROVINCE_RADIUS;
}

export function heartProvinceNeighbours(
  q: number,
  r: number
): readonly HeartProvinceCoordinate[] {
  const neighbours = DIRECTIONS
    .map(([dq, dr]) => ({ q: q + dq, r: r + dr }))
    .filter((coordinate) => isHeartProvinceCoordinate(coordinate.q, coordinate.r))
    .map((coordinate) => Object.freeze(coordinate));
  return Object.freeze(neighbours);
}

export const heartProvinceNeighbors = heartProvinceNeighbours;

export function heartProvinceRole(
  distance: number,
  slot: number
): HeartProvinceRole {
  if (distance === 0) return "throne";
  if (distance === 1) return "citadel";
  if (distance === 2 && slot === 0) return "gate";
  if (distance === 3 && slot === 0) return "guardian";
  if (distance === 2 || distance === 3) return "inner";
  if (distance === 4 && (slot === 1 || slot === 3)) return "pillar";
  if (distance === 9 && (slot === 0 || slot === 2 || slot === 5 || slot === 7)) {
    return "camp";
  }
  if (distance === 12 && slot === 6) return "arrival";
  return "battlefield";
}

function frontForSide(side: number): HeartProvinceFront {
  return (side + 1) as HeartProvinceFront;
}

export function generateHeartProvinceTopology(): HeartProvinceTopology {
  const cells: HeartProvinceCell[] = [];
  let index = 0;

  cells.push(Object.freeze({
    id: heartProvinceCellId(0, 0),
    q: 0,
    r: 0,
    index: index++,
    distance: 0,
    front: null,
    slot: 0,
    role: "throne"
  }));

  for (let distance = 1; distance <= HEART_PROVINCE_RADIUS; distance += 1) {
    let q = 0;
    let r = -distance;
    for (let side = 0; side < DIRECTIONS.length; side += 1) {
      const [dq, dr] = DIRECTIONS[side];
      for (let slot = 0; slot < distance; slot += 1) {
        cells.push(Object.freeze({
          id: heartProvinceCellId(q, r),
          q,
          r,
          index: index++,
          distance,
          front: frontForSide(side),
          slot,
          role: heartProvinceRole(distance, slot)
        }));
        q += dq;
        r += dr;
      }
    }
  }

  if (cells.length !== HEART_PROVINCE_CELL_COUNT) {
    throw new Error("heart_province_topology_cell_count_invalid");
  }

  return Object.freeze({
    schema: HEART_PROVINCE_SCHEMA,
    radius: HEART_PROVINCE_RADIUS,
    cells: Object.freeze(cells)
  });
}

export function heartProvinceCellById(
  topology: HeartProvinceTopology,
  id: string
): HeartProvinceCell | undefined {
  return topology.cells.find((cell) => cell.id === id);
}

export function heartProvinceCellAt(
  topology: HeartProvinceTopology,
  q: number,
  r: number
): HeartProvinceCell | undefined {
  return topology.cells.find((cell) => cell.q === q && cell.r === r);
}

export const lookupHeartProvinceCellById = heartProvinceCellById;
export const lookupHeartProvinceCell = heartProvinceCellAt;

export function heartProvinceNeighbourCells(
  topology: HeartProvinceTopology,
  cellOrId: HeartProvinceCell | string
): readonly HeartProvinceCell[] {
  const cell = typeof cellOrId === "string"
    ? heartProvinceCellById(topology, cellOrId)
    : cellOrId;
  if (!cell) return Object.freeze([]) as readonly HeartProvinceCell[];
  const neighbours = heartProvinceNeighbours(cell.q, cell.r)
    .map((coordinate) => heartProvinceCellAt(topology, coordinate.q, coordinate.r))
    .filter((candidate): candidate is HeartProvinceCell => candidate !== undefined);
  return Object.freeze(neighbours);
}

export function selectHeartProvinceCells(
  topology: HeartProvinceTopology,
  selector: HeartProvinceSelector = {}
): readonly HeartProvinceCell[] {
  const selected = topology.cells.filter((cell) =>
    (selector.front === undefined || cell.front === selector.front) &&
    (selector.role === undefined || cell.role === selector.role)
  );
  return Object.freeze(selected);
}

export function selectHeartProvinceFront(
  topology: HeartProvinceTopology,
  front: HeartProvinceFront
): readonly HeartProvinceCell[] {
  return selectHeartProvinceCells(topology, { front });
}

export function selectHeartProvinceRole(
  topology: HeartProvinceTopology,
  role: HeartProvinceRole
): readonly HeartProvinceCell[] {
  return selectHeartProvinceCells(topology, { role });
}

export function serializeHeartProvinceTopology(
  topology: HeartProvinceTopology
): string {
  return JSON.stringify({
    schema: topology.schema,
    radius: topology.radius,
    cells: topology.cells.map((cell) => ({
      id: cell.id,
      q: cell.q,
      r: cell.r,
      index: cell.index,
      distance: cell.distance,
      front: cell.front,
      slot: cell.slot,
      role: cell.role
    }))
  });
}
